import { useEffect, useState } from 'react';
import contactHero1 from './assets/ContactUsHero_1.png';
import contactHero2 from './assets/ContactUsHero_2.png';
import contactHero3 from './assets/ContactUsHero_3.jpg';
import constructionUpdateImage from './assets/ConstructionUpdate_1.jpg';

function ContactUsPage({ page }) {
  const heroSlides = [contactHero1, contactHero2, contactHero3];
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveSlide((current) => (current + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  useEffect(() => {
    const animatedItems = document.querySelectorAll('.contact-animate');
    if (!animatedItems.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    animatedItems.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  const heroTitle = page?.title || 'Contact Us';
  const heroSummary =
    page?.summary ||
    'We are here to help. Reach out for admissions support, campus visits, or general enquiries.';

  const faqs = page?.faqs || [
    {
      question: 'About Curriculum',
      answer: 'We blend national standards with project-based learning and character education.',
    },
    {
      question: 'Uniforms',
      answer: 'Uniform guidelines are shared during enrollment and updated every academic year.',
    },
    {
      question: 'School Hours',
      answer: 'Regular classes run Monday to Friday with half-day sessions on Saturday.',
    },
    {
      question: 'Admissions Timeline',
      answer: 'Admissions open year-round with priority windows for new academic years.',
    },
  ];

  const partnerships = page?.partnerships || [
    {
      name: 'Local University Network',
      description: 'Strategic partnerships with leading universities for student mentorship, research opportunities, and guest lectures from industry experts.',
      tags: ['Mentorship', 'Research', 'Workshops'],
      link: '/partnerships/universities',
      logo: null,
    },
    {
      name: 'Education Innovation Alliance',
      description: 'Collaborative network focused on curriculum development, teacher professional development, and educational best practices.',
      tags: ['Curriculum', 'Training', 'Innovation'],
      link: '/partnerships/education-alliance',
      logo: null,
    },
    {
      name: 'STEM Community Partners',
      description: 'Industry partnerships providing cutting-edge lab facilities, STEM competitions, and scholarship opportunities for talented students.',
      tags: ['STEM', 'Labs', 'Scholarships'],
      link: '/partnerships/stem',
      logo: null,
    },
    {
      name: 'Global Education Network',
      description: 'International collaboration enabling student exchange programs, cross-cultural learning, and teacher benchmarking with schools worldwide.',
      tags: ['Exchange', 'Global', 'Cultural'],
      link: '/partnerships/global',
      logo: null,
    },
    {
      name: 'Community Development Partners',
      description: 'Local organizations supporting community service initiatives, environmental projects, and social responsibility programs.',
      tags: ['Community', 'Service', 'Sustainability'],
      link: '/partnerships/community',
      logo: null,
    },
    {
      name: 'Technology & Innovation Hub',
      description: 'Tech companies providing digital learning tools, coding bootcamps, and AI-powered educational resources for 21st-century skills.',
      tags: ['Technology', 'Digital', 'AI'],
      link: '/partnerships/technology',
      logo: null,
    },
  ];

  const testimonials = page?.testimonials || [
    {
      name: 'Alya Pradipta',
      role: 'Parent',
      quote: 'The teachers are attentive and the learning environment feels warm and safe.',
      image: contactHero1,
    },
    {
      name: 'Rafi Nugroho',
      role: 'Student',
      quote: 'Projects and clubs help me explore new interests every semester.',
      image: contactHero2,
    },
    {
      name: 'Ibu Lestari',
      role: 'Teacher',
      quote: 'We focus on character education while keeping lessons practical and engaging.',
      image: contactHero3,
    },
    {
      name: 'Nadia Putri',
      role: 'Parent',
      quote: 'Communication from the school is clear, consistent, and helpful.',
      image: contactHero2,
    },
    {
      name: 'Arga Pranoto',
      role: 'Student',
      quote: 'The facilities and labs make learning hands-on and exciting.',
      image: contactHero3,
    },
    {
      name: 'Pak Surya',
      role: 'Teacher',
      quote: 'Our team supports each other and keeps student growth at the center.',
      image: contactHero1,
    },
    {
      name: 'Dinda Maheswari',
      role: 'Alumni',
      quote: 'I felt prepared for university thanks to the guidance and mentoring.',
      image: contactHero2,
    },
    {
      name: 'Kevin Wijaya',
      role: 'Parent',
      quote: 'The school community is welcoming and encourages positive values.',
      image: contactHero3,
    },
  ];

  const getInitials = (value) =>
    String(value || '')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join('');

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [testimonialForm, setTestimonialForm] = useState({
    name: '',
    role: 'Parent',
    message: '',
    imageFile: null,
    imagePreview: '',
  });
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const maxTestimonialWords = 60;
  const maxImageSizeMb = 2;
  const countdownImage = constructionUpdateImage;
  const constructionProgress = Math.min(Math.max(page?.construction_progress ?? 72, 0), 100);
  const constructionMilestones = page?.construction_milestones || [
    'Foundation and structural frame completed',
    'Interior finishing in progress',
    'Lab installations scheduled',
    'Landscape and safety certification next',
  ];
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [faqQuery, setFaqQuery] = useState('');
  const [openFaqIndex, setOpenFaqIndex] = useState(-1);

  const rawWhatsApp = page?.whatsapp_number || '08815130338';
  const normalizedWhatsApp = String(rawWhatsApp || '')
    .replace(/\D/g, '')
    .replace(/^0/, '62');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const lines = [
      'Hello Permata Harapan School,',
      '',
      `Name: ${formData.name || '-'}`,
      `Phone: ${formData.phone || '-'}`,
      `Subject: ${formData.subject || '-'}`,
      '',
      formData.message || '-',
    ];
    const text = encodeURIComponent(lines.join('\n'));
    const url = `https://wa.me/${normalizedWhatsApp}?text=${text}`;
    window.open(url, '_blank', 'noopener');
  };

  const handleTestimonialChange = (event) => {
    const { name, value, files } = event.target;
    if (name === 'imageFile' && files && files[0]) {
      const file = files[0];
      const previewUrl = URL.createObjectURL(file);
      setTestimonialForm((prev) => ({
        ...prev,
        imageFile: file,
        imagePreview: previewUrl,
      }));
      return;
    }
    setTestimonialForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleTestimonialSubmit = (event) => {
    event.preventDefault();
    const wordCount = testimonialForm.message
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;
    if (wordCount > maxTestimonialWords) {
      return;
    }
    if (testimonialForm.imageFile && testimonialForm.imageFile.size > maxImageSizeMb * 1024 * 1024) {
      return;
    }
    setTestimonialForm({ name: '', role: 'Parent', message: '', imageFile: null, imagePreview: '' });
  };

  useEffect(() => {
    if (!testimonials.length) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setActiveTestimonial((current) => (current + 1) % testimonials.length);
    }, 4500);

    return () => clearInterval(intervalId);
  }, [testimonials.length]);

  useEffect(() => {
    const targetDate = new Date('2026-12-01T00:00:00');
    const updateCountdown = () => {
      const now = new Date();
      const diff = Math.max(targetDate.getTime() - now.getTime(), 0);
      const totalSeconds = Math.floor(diff / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      setCountdown({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const intervalId = setInterval(updateCountdown, 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    setOpenFaqIndex(-1);
  }, [faqQuery]);

  const normalizedFaqQuery = faqQuery.trim().toLowerCase();
  const filteredFaqs = faqs.filter((item) => {
    if (!normalizedFaqQuery) {
      return true;
    }
    return (
      String(item.question || '').toLowerCase().includes(normalizedFaqQuery) ||
      String(item.answer || '').toLowerCase().includes(normalizedFaqQuery)
    );
  });

  return (
    <section className="contact-page">
      <section className="admissions-hero" id="contact-overview">
        <div className="admissions-hero-media" aria-hidden="true">
          {heroSlides.map((slide, index) => (
            <div
              key={slide}
              className={`admissions-hero-slide ${index === activeSlide ? 'active' : ''}`}
            >
              <img src={slide} alt="Contact hero" />
            </div>
          ))}
        </div>
        <div className="admissions-hero-overlay" aria-hidden="true" />
        <div className="admissions-hero-content">
          <p className="admissions-hero-kicker">Contact Us</p>
          <h2>{heroTitle}</h2>
          <p>{heroSummary}</p>
          <div className="admissions-hero-dots" role="tablist" aria-label="Contact hero slides">
            {heroSlides.map((slide, index) => (
              <button
                key={slide}
                type="button"
                className={`admissions-hero-dot ${index === activeSlide ? 'active' : ''}`}
                onClick={() => setActiveSlide(index)}
                aria-label={`Show slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="contact-form-section" id="contact-form">
        <div className="contact-form-shell contact-animate">
          <div className="contact-form-header">
            <p className="contact-form-kicker">Contact Form</p>
            <h3>Send us a message</h3>
            <p>
              Share your questions and our team will respond as soon as possible.
            </p>
          </div>

          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="contact-form-row">
              <label>
                Full Name
                <input
                  type="text"
                  name="name"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={handleChange}
                />
              </label>
              <label>
                Phone Number
                <input
                  type="tel"
                  name="phone"
                  placeholder="Your WhatsApp number"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </label>
            </div>
            <label className="contact-form-full">
              Subject
              <input
                type="text"
                name="subject"
                placeholder="How can we help?"
                value={formData.subject}
                onChange={handleChange}
              />
            </label>
            <label className="contact-form-full">
              Message
              <textarea
                name="message"
                rows="5"
                placeholder="Write your message here"
                value={formData.message}
                onChange={handleChange}
              />
            </label>
            <button type="submit" className="contact-form-cta">
              Send via WhatsApp
            </button>
          </form>
        </div>
      </section>

      <section className="contact-location" id="contact-location">
        <div className="contact-location-shell contact-animate">
          <div className="contact-location-header">
            <p className="contact-location-kicker">Get In Touch</p>
            <h3>Visit Us Today</h3>
            <p>A welcoming junior high school in West Jakarta that provides a supportive learning environment where students can grow, learn, and thrive.</p>
          </div>

          <div className="contact-location-container">
            <div className="contact-location-map-box">
              <div className="contact-location-map">
                <iframe
                  title="Permata Harapan School location"
                  src="https://www.google.com/maps?q=Jl.%20Srengseng%20Raya%20No.%2069%20Jakarta&output=embed"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen=""
                />
              </div>
            </div>

            <div className="contact-location-content">
              <div className="contact-info-block">
                <div className="contact-info-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div className="contact-info-text">
                  <h4>Address</h4>
                  <p>Jl. Srengseng Raya No. 69, Kel. Srengseng, Kembangan, Jakarta Barat</p>
                  <a href="https://maps.app.goo.gl/TEkiMjRPHCw7pR4L6" target="_blank" rel="noopener noreferrer" className="contact-info-link">Get Directions →</a>
                </div>
              </div>

              <div className="contact-info-block">
                <div className="contact-info-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </div>
                <div className="contact-info-text">
                  <h4>Phone</h4>
                  <a href="tel:+62215890110">021 5890 1100</a> / <a href="https://wa.me/6281387678876" target="_blank" rel="noopener noreferrer">WA 0813 8767 887</a>
                </div>
              </div>

              <div className="contact-info-block">
                <div className="contact-info-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                </div>
                <div className="contact-info-text">
                  <h4>Email</h4>
                  <a href="mailto:permataharapansecondary@gmail.com">permataharapansecondary@gmail.com</a>
                </div>
              </div>

              <div className="contact-info-block">
                <div className="contact-info-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <div className="contact-info-text">
                  <h4>Office Hours</h4>
                  <p>Mon - Fri: 07:30 - 16:00</p>
                  <p>Sat: 07:30 - 13:00</p>
                </div>
              </div>

              <div className="contact-social-block">
                <h4>Follow Us</h4>
                <div className="contact-social-icons">
                  <a href="https://instagram.com/permataharapan" target="_blank" rel="noopener noreferrer" title="Instagram">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" fill="none" stroke="currentColor" strokeWidth="2" />
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" fill="none" stroke="currentColor" strokeWidth="2" />
                      <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" />
                    </svg>
                  </a>
                  <a href="https://facebook.com/permataharapan" target="_blank" rel="noopener noreferrer" title="Facebook">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18 2h-3a6 6 0 0 0-6 6v3H7v4h2v8h4v-8h3l1-4h-4V8a2 2 0 0 1 2-2h3z" fill="none" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </a>
                  <a href="https://youtube.com/@permataharapan" target="_blank" rel="noopener noreferrer" title="YouTube">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z" fill="none" stroke="currentColor" strokeWidth="2" />
                      <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" fill="currentColor" />
                    </svg>
                  </a>
                  <a href="https://linkedin.com/school/permataharapan" target="_blank" rel="noopener noreferrer" title="LinkedIn">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z" fill="none" stroke="currentColor" strokeWidth="2" />
                      <circle cx="4" cy="4" r="2" fill="currentColor" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="contact-faq" id="contact-faq">
        <div className="contact-faq-shell contact-animate">
          <div className="contact-faq-header">
            <p className="contact-faq-kicker">FAQ</p>
            <h3>Quick answers about school life</h3>
            <p>Get fast answers about curriculum, uniforms, school hours, and more.</p>
          </div>
          <div className="contact-faq-search">
            <input
              type="search"
              placeholder="Search questions"
              value={faqQuery}
              onChange={(event) => setFaqQuery(event.target.value)}
            />
          </div>
          <div className="contact-faq-grid">
            {filteredFaqs.map((item, index) => {
              const isOpen = openFaqIndex === index;
              return (
                <article
                  key={`${item.question}-${index}`}
                  className={`contact-faq-card is-visible ${isOpen ? 'is-open' : ''}`}
                >
                  <button
                    type="button"
                    className="contact-faq-question"
                    onClick={() => setOpenFaqIndex(isOpen ? -1 : index)}
                    aria-expanded={isOpen}
                  >
                    <span>{item.question}</span>
                    <span className="contact-faq-toggle" aria-hidden="true">&gt;</span>
                  </button>
                  {isOpen && (
                    <div className="contact-faq-answer">
                      <p>{item.answer}</p>
                    </div>
                  )}
                </article>
              );
            })}
            {!filteredFaqs.length && (
              <div className="contact-faq-empty">
                <p>No matching questions found. Try another keyword.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="contact-partnerships" id="contact-partnerships">
        <div className="contact-partnerships-shell contact-animate">
          <div className="contact-partnerships-header">
            <p className="contact-partnerships-kicker">Partnerships / Affiliations</p>
            <h3>Universities, organizations, and education networks</h3>
            <p>
              We collaborate with trusted partners to expand learning opportunities, foster innovation, and create meaningful impact in education and community development.
            </p>
          </div>
          <div className="contact-partnerships-grid">
            {partnerships.map((partner, index) => (
              <article 
                key={`${partner.name}-${index}`} 
                className="contact-partnership-card is-visible"
                style={{ '--index': index + 1 }}
              >
                <div className="contact-partnership-top">
                  <div className="contact-partnership-logo">
                    {partner.logo ? (
                      <img src={partner.logo} alt={partner.name} />
                    ) : (
                      <span>{getInitials(partner.name)}</span>
                    )}
                  </div>
                  <div className="contact-partnership-description">
                    <h4 className="contact-partnership-title">{partner.name}</h4>
                    <p className="contact-partnership-text">{partner.description}</p>
                    {partner.tags && partner.tags.length > 0 && (
                      <div className="contact-partnership-meta">
                        {partner.tags.map((tag) => (
                          <span key={tag} className="contact-partnership-tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {partner.link && (
                  <a
                    className="contact-partnership-link"
                    href={partner.link}
                    title={`Learn more about ${partner.name}`}
                  >
                    Learn more
                  </a>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="contact-testimonials contact-testimonials-rebuild" id="contact-testimonials">
        <div className="contact-testimonials-rebuild-shell contact-animate">
          <div className="contact-testimonials-rebuild-header">
            <p className="contact-testimonials-rebuild-kicker">Testimonials</p>
            <h3>Stories that shape our community</h3>
            <p>Parents, students, and teachers share the moments that matter most.</p>
          </div>
          <div className="contact-testimonials-carousel">
            <button
              type="button"
              className="contact-testimonials-arrow left"
              onClick={() =>
                setActiveTestimonial(
                  (current) => (current - 1 + testimonials.length) % testimonials.length
                )
              }
              aria-label="Previous testimonial"
            >
              &lt;
            </button>
            <div className="contact-testimonials-cards">
              {[-1, 0, 1].map((offset) => {
                const index =
                  (activeTestimonial + offset + testimonials.length) % testimonials.length;
                const item = testimonials[index];
                return (
                  <article
                    key={`${item.name}-${index}-${offset}`}
                    className={`contact-testimonials-card ${
                      offset === 0 ? 'is-active' : 'is-side'
                    }`}
                  >
                    <div className="contact-testimonials-avatar">
                      <img src={item.image} alt={item.name} />
                    </div>
                    <p className="contact-testimonials-quote">“{item.quote}”</p>
                    <p className="contact-testimonials-name">{item.name}</p>
                    <p className="contact-testimonials-role">{item.role}</p>
                  </article>
                );
              })}
            </div>
            <button
              type="button"
              className="contact-testimonials-arrow right"
              onClick={() =>
                setActiveTestimonial((current) => (current + 1) % testimonials.length)
              }
              aria-label="Next testimonial"
            >
              &gt;
            </button>
            <div className="contact-testimonials-dots">
              {testimonials.map((_, index) => (
                <button
                  key={`dot-${index}`}
                  type="button"
                  className={`contact-testimonials-dot ${
                    activeTestimonial === index ? 'active' : ''
                  }`}
                  onClick={() => setActiveTestimonial(index)}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
          <div className="contact-testimonials-rebuild-form">
            <div className="contact-testimonials-rebuild-form-header">
              <p>Submit Testimonial</p>
              <h4>Help families discover Permata Harapan</h4>
            </div>
            <div className="contact-testimonial-form-panel">
              <ul className="contact-testimonial-form-tips">
                <li>Share one memorable moment from learning.</li>
                <li>Mention a teacher, program, or activity.</li>
                <li>Upload a smiling photo for the card.</li>
              </ul>
              <form onSubmit={handleTestimonialSubmit} className="contact-testimonial-form">
                <label>
                  Full Name
                  <input
                    type="text"
                    name="name"
                    placeholder="Your name"
                    value={testimonialForm.name}
                    onChange={handleTestimonialChange}
                  />
                </label>
                <label>
                  I am a
                  <select
                    name="role"
                    value={testimonialForm.role}
                    onChange={handleTestimonialChange}
                  >
                    <option value="Parent">Parent</option>
                    <option value="Student">Student</option>
                    <option value="Teacher">Teacher</option>
                    <option value="Alumni">Alumni</option>
                  </select>
                </label>
                <label className="contact-testimonial-full">
                  Testimonial
                  <textarea
                    name="message"
                    rows="4"
                    placeholder="Share your experience"
                    value={testimonialForm.message}
                    onChange={handleTestimonialChange}
                  />
                  <span className="contact-testimonial-hint">
                    Max {maxTestimonialWords} words.
                  </span>
                </label>
                <label className="contact-testimonial-full">
                  Upload Photo
                  <div className="contact-upload-field">
                    <input
                      type="file"
                      name="imageFile"
                      accept="image/jpeg,image/png"
                      required
                      onChange={handleTestimonialChange}
                    />
                    <span className="contact-upload-label">
                      {testimonialForm.imageFile
                        ? testimonialForm.imageFile.name
                        : 'Choose JPG or PNG'}
                    </span>
                    <span className="contact-upload-action">Browse</span>
                  </div>
                  <span className="contact-testimonial-hint">
                    Max {maxImageSizeMb}MB. JPG/PNG only.
                  </span>
                </label>
                <button type="submit" className="contact-testimonial-cta">
                  Send testimonial
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="contact-countdown contact-countdown-rebuild" id="contact-countdown">
        <div className="contact-countdown-rebuild-shell contact-animate">
          <div className="contact-countdown-rebuild-header">
            <p className="contact-countdown-rebuild-kicker">Construction Updates</p>
            <h3>Our Campus Opens in December 2026!</h3>
            <p>Follow our progress as we build the next chapter for learning.</p>
          </div>
          <div className="contact-countdown-rebuild-grid">
            <div className="contact-countdown-rebuild-media">
              <img src={countdownImage} alt="Campus construction" />
              <div className="contact-countdown-rebuild-media-overlay" aria-hidden="true" />
              <div className="contact-countdown-rebuild-progress">
                <div className="contact-countdown-rebuild-progress-track">
                  <span
                    className="contact-countdown-rebuild-progress-fill"
                    style={{ width: `${constructionProgress}%` }}
                  />
                </div>
                <div className="contact-countdown-rebuild-progress-meta">
                  <span>Construction timeline</span>
                  <strong>{constructionProgress}%</strong>
                </div>
              </div>
            </div>
            <div className="contact-countdown-rebuild-panel">
              <div className="contact-countdown-rebuild-stats">
                <div>
                  <span>{countdown.days}</span>
                  <p>Days</p>
                </div>
                <div>
                  <span>{String(countdown.hours).padStart(2, '0')}</span>
                  <p>Hours</p>
                </div>
                <div>
                  <span>{String(countdown.minutes).padStart(2, '0')}</span>
                  <p>Minutes</p>
                </div>
                <div>
                  <span>{String(countdown.seconds).padStart(2, '0')}</span>
                  <p>Seconds</p>
                </div>
              </div>
              <div className="contact-countdown-rebuild-updates">
                <div>
                  <h4>Current Status</h4>
                  <p>Structural work completed. Interior finishing and landscape underway.</p>
                </div>
                <div>
                  <h4>Next Milestone</h4>
                  <p>Classroom setup, lab installations, and safety certifications.</p>
                </div>
              </div>
              <ul className="contact-countdown-rebuild-milestones">
                {constructionMilestones.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </section>
  );
}

export default ContactUsPage;
